import { API_BASE_URL } from '../config/serviceConfig';

// Errore tipizzato che espone il codice HTTP — necessario per distinguere
// 401 (non autenticato) da 422 (validazione) da 500 (server error)
// nei componenti e nell'AuthGuard.
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const getAuthToken = (): string | null => {
  try {
    return (
      localStorage.getItem('bp_token') ?? sessionStorage.getItem('bp_token')
    );
  } catch {
    return null;
  }
};

const buildHeaders = (includeAuth = true): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (includeAuth) {
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Preferisce il messaggio specifico del primo campo in errore (es. "Email già
    // in uso.") al messaggio generico di riepilogo (es. "Dati non validi.").
    const firstFieldError = body?.errors
      ? (Object.values(body.errors)[0] as string[] | undefined)?.[0]
      : undefined;
    throw new ApiError(
      res.status,
      firstFieldError ?? body?.message ?? `HTTP ${res.status}`
    );
  }
  return res.json() as Promise<T>;
};

export const apiClient = {
  get: <T>(path: string): Promise<T> =>
    fetch(`${API_BASE_URL}${path}`, {
      headers: buildHeaders(),
    }).then((r) => handleResponse<T>(r)),

  post: <T>(path: string, body: unknown, auth = true): Promise<T> =>
    fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify(body),
    }).then((r) => handleResponse<T>(r)),

  patch: <T>(path: string, body: unknown): Promise<T> =>
    fetch(`${API_BASE_URL}${path}`, {
      method: 'PATCH',
      headers: buildHeaders(),
      body: JSON.stringify(body),
    }).then((r) => handleResponse<T>(r)),

  delete: <T>(path: string): Promise<T> =>
    fetch(`${API_BASE_URL}${path}`, {
      method: 'DELETE',
      headers: buildHeaders(),
    }).then((r) => handleResponse<T>(r)),

  postForm: <T>(path: string, form: FormData): Promise<T> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: form,
    }).then((r) => handleResponse<T>(r));
  },

  getBlob: (path: string): Promise<Blob> =>
    fetch(`${API_BASE_URL}${path}`, {
      headers: buildHeaders(),
    }).then((r) => {
      if (!r.ok) throw new ApiError(r.status, `HTTP ${r.status}`);
      return r.blob();
    }),
};
